import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { ConversionToK } from "../../utils/shared/costCalculations.utils";

interface WeeklyCostKpiCardProps {
  label: string;
  value: number;
  changePercent: number;
  changeLabel?: string;
  exportMode?: boolean;
}
/**
 * WeeklyCostKpiCard
 *
 * @description Displays KPI summary card with value and week-over-week change.
 * Color scheme matches Summary Dashboard: aqua value, orange/green trend colors.
 *
 * @param label - KPI title (e.g., Total Spend, Avg Daily Cost)
 * @param value - KPI numeric value
 * @param changePercent - Week-over-week percentage change
 * @param changeLabel - Optional label describing comparison (default: "vs last week")
 * @param exportMode - PDF export layout variant
 *
 * @returns KPI card component
 */
const WeeklyCostKpiCard = ({
  label,
  value,
  changePercent,
  changeLabel = "vs last week",
  exportMode = false,
}: WeeklyCostKpiCardProps) => {
  const isDown = changePercent < 0;
  const isNeutral = changePercent === 0;
  const Icon = isNeutral ? Minus : isDown ? TrendingDown : TrendingUp;
  const exportArrow = isNeutral ? "-" : isDown ? "\u2193" : "\u2191";

  // Cost increase = bad (orange/negative), decrease = good (green/positive), neutral = disabled gray
  const changeToneClasses = isNeutral
    ? "text-[var(--text-disabled)]"
    : isDown
      ? "text-[var(--kpi-trend-positive)]"
      : "text-[var(--kpi-trend-negative)]";

  return (
    <div
      className={`min-w-[150px] rounded-[10px] border border-[var(--border-default)] bg-[var(--bg-elevated)] text-center flex flex-col items-center gap-1 ${
        exportMode
          ? "h-24 justify-between px-4 pb-2 pt-[10px]"
          : "justify-center px-4 py-2"
      }`}
    >
      <div className="text-[11px] tracking-[0.3px] text-[var(--kpi-title)]">
        {label}
      </div>
      <div
        className={`text-[20px] font-semibold text-[var(--kpi-value)] leading-none ${exportMode ? "mt-0" : "mt-0.5"}`}
      >
        ${ConversionToK(value)}
      </div>
      <div
        data-testid="kpi-change"
        className={`inline-flex items-center justify-center gap-[3px] whitespace-nowrap border ${
          isNeutral
            ? "border-[var(--text-disabled)]"
            : isDown
              ? "border-[var(--kpi-trend-positive)]"
              : "border-[var(--kpi-trend-negative)]"
        } ${changeToneClasses} ${
          exportMode
            ? "mt-0 mb-0.5 min-h-5 rounded-full px-2.5 py-[3px] text-[11px] font-bold leading-[1.2]"
            : "mt-1 rounded-[5px] px-[7px] py-[2px] text-[10px] font-semibold leading-none"
        }`}
      >
        {exportMode ? (
          <span className="text-[10px] font-bold leading-none">{exportArrow}</span>
        ) : (
          <Icon size={10} strokeWidth={2.5} />
        )}
        {Math.abs(changePercent).toFixed(1)}% {changeLabel}
      </div>
    </div>
  );
};
export default WeeklyCostKpiCard;
